import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, ChevronDown, Clock } from "lucide-react";
import { computeTotals, formatGBP, type Service } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { ServiceIcon } from "../../components/icons";
import { api } from "../../api/client";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import { GarageCallLink } from "./GarageCallLink";
import type { StepProps } from "../stepTypes";
import s from "../steps.module.css";

function ServiceTile({
  service,
  selected,
  onToggle,
}: {
  service: Service;
  selected: boolean;
  onToggle: () => void;
}) {
  const free = service.price === 0;
  return (
    <button
      type="button"
      className={[s.tile, selected ? s.tileSelected : ""].join(" ")}
      onClick={onToggle}
      aria-pressed={selected}
    >
      {service.popular && <span className={s.popularBadge}>Most popular</span>}
      <div className={s.tileHead}>
        <ServiceIcon icon={service.icon} className={s.tileIcon} size={26} />
        <span className={s.tileCheck} aria-hidden="true">
          <Check size={15} strokeWidth={3} />
        </span>
      </div>
      <p className={s.tileName}>{service.name}</p>
      {service.description && (
        <p className={s.tileDesc}>{service.description}</p>
      )}
      <div className={s.tilePrice}>
        {free ? (
          <span className={s.priceFree}>FREE</span>
        ) : (
          <>
            <span className={s.priceNow}>{formatGBP(service.price)}</span>
            {service.priceWas != null && service.priceWas > service.price && (
              <span className={s.priceWas}>{formatGBP(service.priceWas)}</span>
            )}
          </>
        )}
      </div>
      {service.durationMins > 0 && (
        <span className={s.tileDuration}>
          <Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
          approx {formatDuration(service.durationMins)}
        </span>
      )}
    </button>
  );
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Choose or remove services, including when editing a booking. */
export function ServicesStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const garageId = useSession((st) => st.garageId)!;
  const selectedIds = useSession((st) => st.selectedServiceIds);
  const toggleService = useSession((st) => st.toggleService);
  const availableServices = useSession((st) => st.availableServices);
  const branchId = useSession((st) => st.branchId);

  const [services, setServices] = useState<Service[]>(
    config.sandbox ? (config.services ?? []) : availableServices,
  );
  const [expanded, setExpanded] = useState(false);

  // Refresh services from the API (config may carry a snapshot already).
  useEffect(() => {
    if (!config.sandbox) return;
    let alive = true;
    api
      .getServices(garageId)
      .then((list) => {
        if (alive && list.length) setServices(list);
      })
      .catch(() => {
        /* keep config snapshot */
      });
    return () => {
      alive = false;
    };
  }, [garageId, config.sandbox]);

  const core = useMemo(
    () =>
      services
        .filter((sv) => sv.category === "core")
        .sort((a, b) => a.order - b.order),
    [services],
  );
  const visibleCore = config.sandbox ? core : core.slice(0, 4);
  const additional = useMemo(
    () =>
      services
        .filter((sv) => sv.category === "additional")
        .sort((a, b) => a.order - b.order),
    [services],
  );

  const totals = computeTotals(services, selectedIds);
  const hasSelection = selectedIds.length > 0;
  const canContinue = hasSelection;

  // Auto-open the expander if an additional service is already selected.
  useEffect(() => {
    if (additional.some((a) => selectedIds.includes(a.id))) setExpanded(true);
  }, [additional, selectedIds]);

  function handleToggle(svc: Service) {
    toggleService(svc.id, svc.combinable);
    if (!selectedIds.includes(svc.id)) {
      track("service_selected", {
        serviceId: svc.id,
        name: svc.name,
        price: svc.price,
      });
    }
  }

  function handleContinue() {
    if (!canContinue) return;
    saveLead("services");
    onNext();
  }

  const selectedBranch = config.branches.find(
    (branch) => branch.id === branchId,
  );

  return (
    <div>
      <StepHeader
        kicker="Choose your service"
        title={config.copy.servicesTitle}
        subtitle="Pick one or combine a few — prices include parts and labour."
        display={false}
      />

      <div className={s.stack}>
        <div className={s.serviceGrid} role="group" aria-label="Core services">
          {visibleCore.map((svc) => (
            <ServiceTile
              key={svc.id}
              service={svc}
              selected={selectedIds.includes(svc.id)}
              onToggle={() => handleToggle(svc)}
            />
          ))}
        </div>

        {!config.sandbox && (
          <GarageCallLink
            phone={selectedBranch?.phone}
            locationName={selectedBranch?.name}
            reason="Need diagnostics or another repair?"
          />
        )}

        {config.sandbox && additional.length > 0 && (
          <div
            className={[s.expander, expanded ? s.expanderOpen : ""].join(" ")}
          >
            <button
              type="button"
              className={s.expanderBtn}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              <span>
                {config.copy.otherServicesLabel}
                <span style={{ color: "var(--ap-text-soft)", fontWeight: 500 }}>
                  {" "}
                  · {additional.length} more
                </span>
              </span>
              <ChevronDown size={18} className={s.expanderChevron} />
            </button>
            {expanded && (
              <div className={s.expanderBody}>
                <div
                  className={s.serviceGrid}
                  role="group"
                  aria-label="Additional services"
                >
                  {additional.map((svc) => (
                    <ServiceTile
                      key={svc.id}
                      service={svc}
                      selected={selectedIds.includes(svc.id)}
                      onToggle={() => handleToggle(svc)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {hasSelection && (
          <div className={s.runningTotal} aria-live="polite">
            <span className={s.runningTotalLabel}>
              {selectedIds.length}{" "}
              {selectedIds.length === 1 ? "service" : "services"}
              {totals.totalDurationMins > 0
                ? ` · ${formatDuration(totals.totalDurationMins)}`
                : ""}
            </span>
            <span className={s.runningTotalValue}>
              {formatGBP(totals.totalPrice)}
            </span>
          </div>
        )}
      </div>

      <div className={s.actions}>
        <Button
          block
          disabled={!canContinue}
          onClick={handleContinue}
          trailingIcon={<ArrowRight size={18} />}
        >
          {config.copy.ctaContinue}
        </Button>
      </div>
    </div>
  );
}
